<template>
	<!-- A list on a work - observations or interventions - added over time, each resolved when dealt with -->
	<v-card class="p-4">
		<h2 class="text-lg font-bold mb-4">
			<v-icon class="mr-1">{{ config.icon }}</v-icon>
			{{ config.title }}
			<v-chip v-if="openCount" small color="orange" dark class="ml-1">
				{{ openCount }} {{ openCount === 1 ? "deschisa" : "deschise" }}
			</v-chip>
		</h2>
		<v-textarea v-model="newText" :label="config.newLabel" rows="2" auto-grow outlined dense
			hide-details></v-textarea>
		<div class="flex flex-row justify-end mt-2 mb-2">
			<!-- dark only when enabled: dark + disabled draws white text on the grey and the button vanishes -->
			<v-btn small color="teal" :dark="!!newText.trim()" :loading="saving" :disabled="!newText.trim()" @click="add">
				<v-icon left small>mdi-plus</v-icon>
				Adauga
			</v-btn>
		</div>
		<p v-if="!items.length" class="text-sm text-gray-600 mb-0">{{ config.empty }}</p>
		<div v-for="o in items" :key="o.id_observation" class="note" :class="{ 'note--resolved': o.resolved == 1 }">
			<v-checkbox :input-value="o.resolved == 1" class="mt-0 pt-0" hide-details dense color="green"
				:title="o.resolved == 1 ? 'Redeschide' : 'Marcheaza rezolvata'"
				@change="resolve(o, $event)"></v-checkbox>
			<div class="flex-1 min-w-0">
				<!-- opened when the montage was marked finished with problems -->
				<v-chip v-if="o.origin === 'montaj'" x-small label color="teal" dark class="mb-1">La montaj</v-chip>
				<!-- left when an act aditional changed the offer after the measurements -->
				<v-chip v-else-if="o.origin === 'act_aditional'" x-small label color="orange" dark class="mb-1">Act aditional</v-chip>
				<div class="note__text whitespace-pre-line">{{ o.text }}</div>
				<div class="text-xs text-gray-600">
					{{ o.created_by_name || "-" }} &middot; {{ o.created_at | formatDateTime }}
					<template v-if="o.resolved == 1">
						<br>Rezolvata de {{ o.resolved_by_name || "-" }} &middot; {{ o.resolved_at | formatDateTime }}
					</template>
				</div>
			</div>
			<v-btn v-if="canDelete(o)" icon x-small title="Sterge" @click="remove(o)">
				<v-icon x-small>mdi-close</v-icon>
			</v-btn>
		</div>
	</v-card>
</template>

<script>
const KINDS = {
	observatie: {
		title: "Observatii",
		icon: "mdi-note-text-outline",
		newLabel: "Observatie noua",
		empty: "Nicio observatie.",
		added: "Observatie adaugata",
		confirmDelete: "Stergi observatia?",
	},
	// problems the client reports, mostly after the work is finished (warranty)
	interventie: {
		title: "Interventii",
		icon: "mdi-tools",
		newLabel: "Interventie noua",
		empty: "Nicio interventie.",
		added: "Interventie adaugata",
		confirmDelete: "Stergi interventia?",
	},
};

export default {
	name: "WorkNotesCard",
	props: {
		work: { type: Object, required: true },
		kind: { type: String, default: "observatie" },
	},
	data() {
		return {
			newText: "",
			saving: false,
		};
	},
	computed: {
		config() {
			return KINDS[this.kind];
		},
		user() {
			return this.$store.getters.loggedUser;
		},
		// the work's list holds both kinds; rows from before kinds existed are observations
		items() {
			return (this.work.observations || []).filter((o) => (o.kind || "observatie") === this.kind);
		},
		openCount() {
			return this.items.filter((o) => o.resolved != 1).length;
		},
	},
	methods: {
		async add() {
			this.saving = true;
			try {
				await this.$store.dispatch("works/addObservation", {
					workId: this.work.id_work,
					kind: this.kind,
					text: this.newText.trim(),
					message: this.config.added,
				});
				this.newText = "";
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.saving = false;
			}
		},
		resolve(o, resolved) {
			this.$store
				.dispatch("works/resolveObservation", { workId: this.work.id_work, id: o.id_observation, resolved: !!resolved })
				.catch(() => { });
		},
		// its author, or an admin/superuser (the API checks the same)
		canDelete(o) {
			return o.created_by == this.user.id_user || ["admin", "superuser"].includes(this.user.rol);
		},
		remove(o) {
			if (!confirm(this.config.confirmDelete)) return;
			this.$store.dispatch("works/deleteObservation", { workId: this.work.id_work, id: o.id_observation }).catch(() => { });
		},
	},
};
</script>

<style scoped>
/* one entry: tick box, text with who/when, delete */
.note {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 8px;
	padding: 8px 0;
	border-top: 1px solid #eee;
}

.note--resolved .note__text {
	color: #9e9e9e;
	text-decoration: line-through;
}
</style>
